/**
 * Alert Store
 * Zustand store for managing wallet tracker alerts
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type AlertCondition =
  | 'any_transaction'
  | 'swap'
  | 'transfer_in'
  | 'transfer_out'
  | 'value_threshold'
  | 'token_buy'
  | 'token_sell'
  | 'token_transfer';

export type AlertChannel = 'in_app' | 'email' | 'webhook';

export interface Alert {
  id: string;
  walletAddress: string;
  walletLabel?: string;
  condition: AlertCondition;
  valueThreshold?: number; // in USD
  enabled: boolean;
  channels: AlertChannel[];
  createdAt: string;
  lastTriggered?: string;
  triggerCount: number;
  // Token-specific tracking
  tokenAddress?: string;
  tokenSymbol?: string;
  tokenName?: string;
}

export interface AlertNotification {
  id: string;
  alertId: string;
  walletAddress: string;
  walletLabel?: string;
  type: AlertCondition;
  message: string;
  transactionHash?: string;
  value?: number;
  timestamp: string;
  read: boolean;
}

interface AlertStore {
  // State
  alerts: Alert[];
  notifications: AlertNotification[];
  showNotifications: boolean;

  // Alert Actions
  addAlert: (alert: Omit<Alert, 'id' | 'createdAt' | 'triggerCount'>) => void;
  removeAlert: (alertId: string) => void;
  updateAlert: (alertId: string, updates: Partial<Alert>) => void;
  toggleAlert: (alertId: string) => void;
  getAlertsForWallet: (walletAddress: string) => Alert[];

  // Notification Actions
  addNotification: (notification: Omit<AlertNotification, 'id' | 'timestamp' | 'read'>) => void;
  markNotificationAsRead: (notificationId: string) => void;
  markAllNotificationsAsRead: () => void;
  removeNotification: (notificationId: string) => void;
  clearAllNotifications: () => void;
  getUnreadCount: () => number;

  // UI State
  setShowNotifications: (show: boolean) => void;
}

export const useAlertStore = create<AlertStore>()(
  persist(
    (set, get) => ({
      // Initial State
      alerts: [],
      notifications: [],
      showNotifications: false,

      // Alert Actions
      addAlert: (alertData) =>
        set((state) => ({
          alerts: [
            ...state.alerts,
            {
              ...alertData,
              id: crypto.randomUUID(),
              createdAt: new Date().toISOString(),
              triggerCount: 0,
            },
          ],
        })),

      removeAlert: (alertId) =>
        set((state) => ({
          alerts: state.alerts.filter((a) => a.id !== alertId),
        })),

      updateAlert: (alertId, updates) =>
        set((state) => ({
          alerts: state.alerts.map((a) =>
            a.id === alertId ? { ...a, ...updates } : a
          ),
        })),

      toggleAlert: (alertId) =>
        set((state) => ({
          alerts: state.alerts.map((a) =>
            a.id === alertId ? { ...a, enabled: !a.enabled } : a
          ),
        })),

      getAlertsForWallet: (walletAddress) => {
        const state = get();
        return state.alerts.filter(
          (a) => a.walletAddress.toLowerCase() === walletAddress.toLowerCase() && a.enabled
        );
      },

      // Notification Actions
      addNotification: (notificationData) =>
        set((state) => {
          // Update alert trigger count and lastTriggered
          const updatedAlerts = state.alerts.map((a) =>
            a.id === notificationData.alertId
              ? {
                  ...a,
                  triggerCount: a.triggerCount + 1,
                  lastTriggered: new Date().toISOString(),
                }
              : a
          );

          return {
            alerts: updatedAlerts,
            notifications: [
              {
                ...notificationData,
                id: crypto.randomUUID(),
                timestamp: new Date().toISOString(),
                read: false,
              },
              ...state.notifications,
            ].slice(0, 100), // Keep only last 100 notifications
          };
        }),

      markNotificationAsRead: (notificationId) =>
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === notificationId ? { ...n, read: true } : n
          ),
        })),

      markAllNotificationsAsRead: () =>
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
        })),

      removeNotification: (notificationId) =>
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== notificationId),
        })),

      clearAllNotifications: () =>
        set({ notifications: [] }),

      getUnreadCount: () => {
        const state = get();
        return state.notifications.filter((n) => !n.read).length;
      },

      // UI State
      setShowNotifications: (show) =>
        set({ showNotifications: show }),
    }),
    {
      name: 'edouard_ai_alerts',
      version: 1,
    }
  )
);
